import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, Trash2, Upload, Lock, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/dash/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ReasonDialog } from "../../components/dash/ReasonDialog";
import { uploadFile } from "../../lib/field";
import { formatDay } from "../../lib/labels";
import { LandMapView, Legend, STATE_COLORS, EXCLUSION_COLOR } from "../../components/map/LandMap";
import { ProjectRow, CapacitySummary, AreaStatePill } from "./LandProjects";
import { LandStatusPill, CapacityBar, LAND_CATEGORY, OWNERSHIP, HISTORY_LABELS, n, fileUrl } from "./shared";
import { ProjectFromLand } from "./ProjectFromLand";
import { PlantingSummary, PlantingComparison, PlantingPreferences } from "../../components/PlantingSystem";
import { ClimateView } from "../../components/Elevation";
import { RainCalendar } from "../../components/RainCalendar";
import { SuitabilityPanel } from "../../components/Suitability";
import { AIInspectorPanel } from "../../components/AIInspector";

const Box = ({ title, action, children, testid }) => (
  <section className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6" data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-4"><h2 className="text-base md:text-lg font-bold text-slate-900">{title}</h2>{action}</div>
    {children}
  </section>
);
const Info = ({ k, v }) => <div><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k}</p><p className="mt-0.5 text-sm font-semibold text-slate-900 break-words">{v || "-"}</p></div>;

const Uploader = ({ land, kinds, onDone }) => {
  const [kind, setKind] = useState("CONSENT");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    const extra = { kind, caption };
    if (kind === "GPS_DOC" && land.latitude != null) Object.assign(extra, { latitude: land.latitude, longitude: land.longitude });
    try { await uploadFile(`/lands/${land.id}/files`, file, extra); toast.success("File diunggah"); setCaption(""); setFile(null); e.target.reset(); onDone(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="grid sm:grid-cols-4 gap-2 mb-4" data-testid="land-file-form">
      <select value={kind} onChange={(e) => setKind(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="land-file-kind">
        {Object.entries(kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <Input placeholder="Keterangan" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="land-file-caption" />
      <Input type="file" required accept="image/*,application/pdf" capture={undefined} onChange={(e) => setFile(e.target.files[0] || null)} data-testid="land-file-input" />
      <Button disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="land-file-upload-btn"><Upload className="h-4 w-4 mr-1" />Unggah</Button>
    </form>
  );
};

const Files = ({ land, meta, farmer, reload }) => {
  const del = (id) => api.delete(`/land-files/${id}`).then(reload).catch((e) => toast.error(formatApiError(e)));
  return (
    <Box title="Dokumen & Foto Lahan" testid="land-files-section">
      {farmer && meta && <Uploader land={land} kinds={meta.fileKinds} onDone={reload} />}
      {!land.files.length && <p className="text-sm text-slate-500">Belum ada file.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {land.files.map((f) => (
          <div key={f.id} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`land-file-${f.id}`}>
            <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="block h-28 bg-slate-100">
              {f.contentType?.startsWith("image/") ? <img src={fileUrl(f.id)} alt={f.caption} className="h-full w-full object-cover" loading="lazy" />
                : <span className="h-full flex items-center justify-center text-slate-500"><FileText className="h-8 w-8" /></span>}
            </a>
            <div className="p-2 text-xs">
              <p className="font-semibold text-slate-800 flex items-center gap-1">{f.isPrivate && <Lock className="h-3 w-3 text-amber-700" />}{meta?.fileKinds[f.kind] || f.kind}</p>
              {f.caption && <p className="text-slate-500 truncate">{f.caption}</p>}
              {farmer && <button onClick={() => del(f.id)} className="mt-1 text-red-700 inline-flex items-center gap-1" data-testid={`land-file-delete-${f.id}`}><Trash2 className="h-3 w-3" />Hapus</button>}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500"><Lock className="h-3 w-3 inline" /> Dokumen legalitas dan surat persetujuan bersifat pribadi, tidak tampil ke investor/publik.</p>
    </Box>
  );
};

const Actions = ({ land, reload }) => {
  const [dlg, setDlg] = useState(null);
  const post = async (url, body, msg) => {
    try { await api.post(url, body); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const active = land.status !== "INACTIVE";
  return (
    <div className="flex flex-wrap gap-2">
      <Link to="ubah" className="inline-flex h-10 items-center px-4 rounded-full border border-slate-200 bg-white text-sm font-semibold" data-testid="land-edit-btn">Edit</Link>
      {active && !land.published && <Button onClick={() => post(`/lands/${land.id}/publish`, {}, "Lahan dipublikasikan")} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="land-publish-btn">Publikasikan</Button>}
      {active && land.published && <Button variant="outline" className="rounded-full" onClick={() => setDlg("unpublish")} data-testid="land-unpublish-btn">Tarik Publikasi</Button>}
      <Button variant="outline" className={`rounded-full ${active ? "text-red-700" : ""}`} onClick={() => setDlg(active ? "deactivate" : "reactivate")} data-testid="land-toggle-btn">{active ? "Nonaktifkan" : "Aktifkan Kembali"}</Button>
      <ReasonDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} danger={dlg === "deactivate"}
        title={{ unpublish: "Tarik publikasi lahan", deactivate: "Nonaktifkan lahan", reactivate: "Aktifkan kembali lahan" }[dlg] || ""} confirmLabel="Konfirmasi"
        onConfirm={(reason) => post(dlg === "unpublish" ? `/lands/${land.id}/unpublish` : `/lands/${land.id}/status/${dlg}`, { reason }, "Status lahan diperbarui")} />
    </div>
  );
};

const MapPdfButton = ({ land }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/lands/${land.id}/map-pdf`, { responseType: "blob", timeout: 90000 });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Peta-Lahan-${land.landCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Peta lahan PDF diunduh");
    } catch (e) { toast.error("Gagal membuat PDF peta lahan. Coba lagi."); } finally { setBusy(false); }
  };
  return (
    <Button type="button" variant="outline" onClick={go} disabled={busy} className="rounded-full border-emerald-800 text-emerald-800" data-testid="land-map-pdf-btn">
      {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}{busy ? "Membuat PDF..." : "Unduh Peta PDF"}
    </Button>
  );
};

export default function LandDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const farmer = user.role === "FARMER_GROUP";
  const [land, setLand] = useState(null);
  const [meta, setMeta] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get(`/lands/${id}`).then((r) => setLand(r.data)).catch((e) => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); api.get("/lands/meta").then((r) => setMeta(r.data)); }, [load]);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!land || params.get("buat") !== "1") return;
    setParams({}, { replace: true });
    if (farmer && land.published && land.status !== "INACTIVE" && land.remainingTrees > 0) {
      setCreating(true);
      setTimeout(() => document.querySelector('[data-testid="land-projects-section"]')?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [land, params, setParams, farmer]);
  if (!land) return <p className="text-sm text-slate-500" data-testid="land-loading">Memuat...</p>;
  const point = land.latitude != null ? [land.latitude, land.longitude] : null;
  const overlays = [{ points: land.boundary, color: (STATE_COLORS[land.colorState] || STATE_COLORS.FREE)[0], fill: 0.1, label: "Batas lahan" },
    ...(land.exclusions || []).map((e) => ({ points: e, color: EXCLUSION_COLOR, fill: 0.35, dashed: true, label: "Tidak dapat ditanami" })),
    ...land.projects.filter((p) => p.area).map((p) => ({ points: p.area, color: STATE_COLORS[p.areaState][0], fill: 0.45, label: `${p.projectCode} · ${STATE_COLORS[p.areaState][1]}` }))];
  const canProject = farmer && land.published && land.status !== "INACTIVE" && land.remainingTrees > 0;
  return (
    <div className="space-y-6" data-testid="land-detail-page">
      <Link to=".." relative="path" className="inline-flex items-center gap-1 text-sm text-emerald-800 font-semibold" data-testid="land-back"><ArrowLeft className="h-4 w-4" />Semua lahan</Link>
      <PageHeader title={`${land.landCode} · ${land.name}`} subtitle={`${land.farmerGroupName || ""} · ${[land.village, land.district, land.regency, land.province].filter(Boolean).join(", ")}`}
        action={<LandStatusPill status={land.landStatus} testid="land-detail-status" />} />
      {farmer && land.selfManaged !== false && <Actions land={land} reload={load} />}
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs sm:text-sm text-amber-900" data-testid="land-disclaimer">{land.disclaimer}</p>
      <Box title="Peta & Koordinat" testid="land-map-section" action={<MapPdfButton land={land} />}>
        <LandMapView point={point} overlays={overlays} testid="land-detail-map" />
        <div className="mt-3 flex flex-wrap items-center gap-3"><span className="text-xs font-semibold text-slate-600">Status lahan:</span><AreaStatePill state={land.colorState} testid="land-color-state" /></div>
        <div className="mt-2"><Legend exclusion /></div>
        <p className="mt-2 text-xs font-mono text-slate-600" data-testid="land-detail-coords">Titik: {point ? `${point[0]}, ${point[1]}` : "-"} · Batas: {land.boundary?.length || 0} titik{land.boundaryAreaHectares ? ` (±${n(land.boundaryAreaHectares)} ha)` : ""}</p>
      </Box>
      <Box title="Kapasitas Tanam" testid="land-capacity-section">
        <div className="mb-4"><CapacitySummary land={land} /></div>
        <div className="mb-4"><PlantingSummary planting={land.planting} plantingSystem={land.plantingSystem} testid="land-planting-summary" /></div>
        <div className="mb-4"><PlantingComparison effHa={land.effectiveAreaHectares} selected={land.plantingSystem} testid="land-compare" /></div>
        <div className="mb-4"><PlantingPreferences landId={land.id} role={user.role} /></div>
        <div className="mb-4"><SuitabilityPanel landId={land.id} role={user.role} landCode={land.landCode} /></div>
        {land.climate && <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 text-sm"><ClimateView c={land.climate} testid="land-climate" /></div>}
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 text-sm"><RainCalendar cal={land.rainCalendar} testid="land-rain-calendar" /></div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <Info k={land.plantingSystem ? `Estimasi sistem (${n(land.planting?.treesPerHectare)}/ha)` : "Estimasi sistem Legacy (1.600/ha)"} v={land.capacityEstimate != null && `${n(land.capacityEstimate)} batang`} />
          <Info k="Kapasitas final" v={`${n(land.capacity)} batang`} />
          {land.capacityOverride != null && <Info k="Alasan koreksi" v={land.capacityOverrideReason} />}
        </div>
        <p className="text-xs text-slate-500">Belum dialokasikan ke kebun: <b data-testid="land-detail-unallocated">{n(land.remainingTrees)} batang</b></p>
      </Box>
      {["SUPER_ADMIN", "FARMER_GROUP", "INDIVIDUAL_MANAGER"].includes(user.role) && <AIInspectorPanel land={land} role={user.role} />}
      <Box title="Data Lahan & Pemilik">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Info k="Kategori" v={LAND_CATEGORY[land.landCategory]} /><Info k="Pemilik lahan" v={land.ownerName} />
          <Info k="Status kepemilikan" v={OWNERSHIP[land.ownershipStatus]} /><Info k="Kontak pemilik" v={land.ownerPhone} />
          <Info k="Ketinggian" v={land.elevation != null && `${n(land.elevation)} mdpl`} /><Info k="Kemiringan" v={land.slope} />
          <Info k="Akses jalan" v={land.accessRoad} /><Info k="Sumber air" v={land.waterAccess} />
          <Info k="Kondisi lahan" v={land.landCondition} /><Info k="Kondisi tanaman" v={land.plantCondition} />
        </div>
      </Box>
      <Files land={land} meta={meta} farmer={farmer} reload={load} />
      <Box title={`Kebun di Lahan Ini (${land.projects.length})`} testid="land-projects-section"
        action={canProject && !creating && <Button onClick={() => setCreating(true)} className="rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="create-project-from-land-btn">Buat Rencana Kebun dari Lahan</Button>}>
        {farmer && !land.published && <p className="text-sm text-amber-800 mb-3">Publikasikan lahan terlebih dahulu untuk membuat kebun.</p>}
        {creating && <div className="mb-4"><ProjectFromLand land={land} onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} /></div>}
        <div className="divide-y divide-slate-100">
          {land.projects.map((p) => <ProjectRow key={p.id} p={p} bookings={land.bookings || []} reload={load} />)}
          {!land.projects.length && <p className="text-sm text-slate-500">Belum ada kebun.</p>}
        </div>
      </Box>
      <Box title="Riwayat" testid="land-history-section">
        <ol className="space-y-2">
          {[...(land.history || [])].reverse().map((h, i) => (
            <li key={i} className="text-sm"><b>{HISTORY_LABELS[h.action] || h.action}</b> · {h.byName || "-"} · <span className="text-slate-500">{formatDay(h.at)}</span>{h.note && <span className="block text-xs text-slate-600">{h.note}</span>}</li>
          ))}
        </ol>
      </Box>
    </div>
  );
}
